<template>
  <main>
    <div class="l-main-content__inner">
      <div class="c-page-header">
        <h1 class="c-page-header-title">学級別 学習状況</h1>
        <div class="c-page-header__return">
          <a
            v-if="learningStatusYearUrl"
            :href="learningStatusYearUrl"
            class="c-button-white -return -teacher"
          >
            <span class="label">戻る</span>
          </a>
        </div>
      </div>

        <dl class="p-learning-filtering -no-gap">
          <div class="item">
            <dt class="item__title">学年</dt>
            <dd class="item__data">
              <p class="val">
                <span v-if="displayAcademicYear" class="val__num">{{
                  displayAcademicYear
                }}</span
                >年
              </p>
            </dd>
          </div>

          <select-single
            v-if="learningStatusClassStore.classConditions"
            title="学級"
            :options="learningStatusClassStore.classConditions.classrooms"
            v-model:selected="classroom"
            @change="changeClassroom"
            :init-blank="false"
            :wide="true"
          ></select-single>
        </dl>

      <section>
        <subject-tabs :subject="subject" @click="clickSubject"></subject-tabs>

        <div class="c-parts-box -white -learning">
          <div id="all_slide" class="p-learning-detail">
            <form action="">
              <period-switches
                :period="period"
                @click="clickPeriod"
              ></period-switches>

              <!-- 教材 -->
              <learning-status-work-selector
                :academicSessionId="academicSessionId"
                :academicYear="academicYear"
                :subject="subject"
                v-model:workId="workId"
                @change="changeWork"
              ></learning-status-work-selector>

              <div class="p-learning-switch">
                <select-single
                  :options="correctAnswerRateOptions"
                  v-model:selected="correctAnswerRateType"
                  :init-blank="false"
                  @change="changeCorrectAnswerRateType"
                ></select-single>
              </div>

              <template v-if="learningStatusClassStore.progressData">
                <!-- 学級に生徒が登録されている場合のみ表示 -->
                <template v-if="learningStatusClassStore.progressData.datasets.length > 0">
                  <!-- 学習状況: 散布図 -->
                  <learning-status-scatter-chart
                    :chartMeta="classProgressChartMeta"
                    :chartData="learningStatusClassStore.progressData"
                    :correctAnswerRateType="correctAnswerRateType"
                    :periodType="period"
                  />

                  <!-- 学習状況: 表 -->
                  <learning-status-table
                    :class-progress-data="learningStatusClassStore.progressData"
                    :period="period"
                  />
                  <div class="c-form-area" style="display: none">
                    <!-- TODO: CSVダウンロード機能の実装 -->
                    <div class="c-form-area__footer">
                      <div class="submitBtn">
                        <!-- CSVダウンロードは開発中のためstyleでデザイン指定追加。機能実装後にstyleを消去する。 -->
                        <button
                          type="button"
                          name="button"
                          class="c-button-blue -prefix-icon -download"
                          style="background: lightgray; pointer-events: none;"
                        >
                          <span class="label">CSVダウンロード</span>
                        </button>
                        <!-- CSVダウンロードは開発中のためメッセージ追加。機能実装後に消去する。 -->
                        <div style="color: red;">※CSVダウンロード機能は現在開発中です。</div>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="c-frame-message">
                    <p class="c-parts-error-message -center">この学級には生徒が登録されていません。</p>
                  </div>
                </template>
              </template>
            </form>
          </div>
        </div>
      </section>
    </div>
    <error-modal></error-modal>
  </main>
</template>

<script setup lang="ts">
  import {ref, computed, onMounted} from "vue";
  import { SUBJECTS, SUBJECT_ALL } from "@/consts/subject";
  import { PERIODS } from "@/consts/display_period";
  import { ScatterChartMeta } from "@/components/teacher/LearningStatusScatterChart.vue";
  import { CORRECT_ANSWER_RATE_TYPE } from "@/consts/correct_answer_rate";
  import { useLearningStatusClassStore } from "@/store/teacher/learning_status_class_store";

  const learningStatusClassStore = useLearningStatusClassStore()
  // 教材セレクトボックスの教材取得のみに使用する。グラフ・表データ取得には使用しない (NFR-3801、NFR-7275)
  const academicSessionId = ref<number | null>(null);
  const academicYear = ref<number | null>(null);
  const classroom = ref("");
  const subject = ref(SUBJECT_ALL.value);
  const period = ref(PERIODS[0].value);
  const workId = ref<number|string>('');
  const correctAnswerRateOptions = ref(CORRECT_ANSWER_RATE_TYPE);
  const correctAnswerRateType = ref(CORRECT_ANSWER_RATE_TYPE[0].value);

  const classProgressChartMeta = ref<ScatterChartMeta>({
    chartId: "progress-scatter"
  });

  const learningStatusYearUrl = computed(() => {
    // 戻るボタン用URL取得
    if (learningStatusClassStore.learningStatusYearBaseUrl) {
      return (
        learningStatusClassStore.learningStatusYearBaseUrl +
        "?" +
        getQueryString(getLearningStatusYearQueryParams())
      );
    }
  })

  const displayAcademicYear = computed(() => {
    if (learningStatusClassStore.classConditions) {
      return learningStatusClassStore.classConditions.academicYears.find(
        newAcademicYear => parseInt(newAcademicYear.value) === academicYear.value
      ).value;
    }
  })

  function getQueryString(queryParams) {
    // 渡されたクエリパラメータ用データをクエリ文字列化する
    return Object.entries(queryParams)
      .map(queryParam => `${queryParam[0]}=${queryParam[1]}`)
      .join("&");
  }

  function getLearningStatusYearQueryParams() {
    return {
      academic_session_id: academicSessionId.value,
      academic_year: academicYear.value,
      subject: subject.value,
      period: period.value,
      ...(workId.value !== '' && { work: workId.value }),
    };
  }

  function getLearningStatusClassQueryParams() {
    return {
      classroom: classroom.value,
      academic_session_id: academicSessionId.value,
      academic_year: academicYear.value,
      subject: subject.value,
      period: period.value,
      ...(workId.value !== '' && { work: workId.value }),
      correct_answer_rate_type: correctAnswerRateType.value,
    };
  }

  function fetchProgressData() {
    const payload = {
      classroom_uuid: classroom.value,
      subject: subject.value,
      period: period.value,
      work: workId.value !== '' ? workId.value : undefined,
    };
    learningStatusClassStore.fetchLearningStatusClassProgressData(payload);
  }

  onMounted(async() => {
    // Data properties にクエリパラメーターをセット
    setInitialDataFromSearchParams();
    // 検索条件 (学級一覧) を取得
    await learningStatusClassStore.fetchLearningStatusClassConditions({
      academic_year: academicYear.value
    });
    // 学習状況取得
    searchLearningStatus();
  })

  function setInitialDataFromSearchParams() {
    // クエリパラメーターから初期値を設定
    // cf. https://developer.mozilla.org/ja/docs/Web/API/URL/searchParams
    let params = new URL(location.toString()).searchParams;

    if (params.has("classroom")) {
      const found = params.get("classroom");
      classroom.value = found ? params.get("classroom") : null;
    }
    if (params.has("academic_session_id")) {
      const parsed = parseInt(params.get("academic_session_id"), 10);
      academicSessionId.value = !isNaN(parsed) ? parsed : null;
    }
    if (params.has("academic_year")) {
      const parsed = parseInt(params.get("academic_year"), 10);
      academicYear.value = !isNaN(parsed) ? parsed : null;
    }
    if (params.has("subject")) {
      const found = SUBJECTS.find(
        subject => subject.value === params.get("subject")
      );
      subject.value = found ? params.get("subject") : SUBJECT_ALL.value;
    }
    if (params.has("period")) {
      const parsed = parseInt(params.get("period"), 10);
      const found = PERIODS.find(period => period.value === parsed);
      period.value = !isNaN(parsed) && found ? parsed : PERIODS[0].value;
    }
    if (params.has("work")) {
      const parsed = parseInt(params.get("work"), 10);
      workId.value = !isNaN(parsed) ? parsed : '';
    }
    if (params.has("correct_answer_rate_type")) {
      const parsed = parseInt(params.get("correct_answer_rate_type"), 10);
      const found = CORRECT_ANSWER_RATE_TYPE.find(answer_rate_type => answer_rate_type.value === parsed);
      correctAnswerRateType.value = !isNaN(parsed) && found ? parsed : CORRECT_ANSWER_RATE_TYPE[0].value;
    }
  }

  // 教科ボタン操作時の処理
  function clickSubject(value) {
    subject.value = value;
    workId.value = '';
    searchLearningStatus();
  }

  // 表示期間ボタン操作時の処理
  function clickPeriod(value) {
    period.value = value;
    searchLearningStatus();
  }

  // 学級セレクトボックス操作時の処理
  function changeClassroom() {
    searchLearningStatus();
  }

  // 教材セレクトボックス操作時の処理
  function changeWork() {
    searchLearningStatus();
  }

  // グラフに表示する正答率の種類のセレクトボックス操作時の処理
  function changeCorrectAnswerRateType() {
    // FIXME: 「【T8-2 学習状況（学級）】表・散布図データ取得API」は、
    //  初回と最新と両方のデータを返すので、ここでAPIコールする必要はなさそう
    searchLearningStatus();
  }

  function updateDocumentTitle() {
    // Document.Title 更新
    const foundSubject = SUBJECTS.find(
      newSubject => newSubject.value === subject.value
    );
    const subjectName = foundSubject ? foundSubject.name : SUBJECT_ALL.name;

    let classroomName = "";

    if (learningStatusClassStore.classConditions) {
      const foundClassroom = learningStatusClassStore.classConditions.classrooms.find(
        newClassroom => newClassroom.value === classroom.value
      );
      classroomName = foundClassroom ? foundClassroom.name : "";
    }
    // NFR-1050 で画面遷移後でも学級名が表示される様にする
    document.title = `学習状況 ${classroomName} ${subjectName} - MANAVIRIA`;
  }

  function searchLearningStatus() {
    if (!learningStatusClassStore.classConditions) {
      return
    }
    fetchProgressData();

    // アドレスバーのURL, ドキュメント.タイトルにも検索条件を反映する
    // https://developer.mozilla.org/ja/docs/Web/API/History/replaceState
    history.replaceState(
      getLearningStatusClassQueryParams(),
      "",
      `?${getQueryString(getLearningStatusClassQueryParams())}`
    );
    updateDocumentTitle();
  }
</script>

<style lang="scss" scoped>
</style>
